import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Alert, Box, Collapse, Link, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import type { ApplicationTabKey } from '../../lib/applicationDetailTabStatus';
import type { TabNarrative, TabNarrativeTone } from '../../lib/applicationTabNarratives';
import { pdlDeclineAlertPulseSx } from '../../lib/pdlDecisionChrome';
import { ApplicationSectionHeader } from './applicationSectionChrome';

function severityOf(tone: TabNarrativeTone): 'success' | 'info' | 'warning' | 'error' {
  switch (tone) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'info';
  }
}

/** Visible supporting lines under the headline — detail (what/why) then bullets (action). */
export function buildTabWhatsHappeningSupportLines(
  narrative: Pick<TabNarrative, 'detail' | 'bullets'>,
  rich = false,
): string[] {
  const bullets = narrative.bullets.filter((b) => b.trim());
  const detail = narrative.detail?.trim() || null;

  if (rich) {
    return bullets.length > 0 ? bullets.slice(0, 4) : detail ? [detail] : [];
  }

  const lines: string[] = [];
  if (detail) lines.push(detail);
  lines.push(...bullets);
  return lines.slice(0, 5);
}

/**
 * Plain-language status at the top of an application detail tab.
 * Default: headline + supporting detail / bullets.
 * Device may pass `rich` to prefer bullets; Identity also surfaces Mitek failure bullets.
 * Hidden when the step passed cleanly with no supporting bullets — attempt UI already says that.
 */
/** The customer's own numbers, laid out like a receipt: label left, amount right. */
function NarrativeCalculation({
  calculation,
  isError,
}: {
  calculation: NonNullable<TabNarrative['calculation']>;
  isError: boolean;
}) {
  const theme = useTheme();
  const accent = isError ? theme.palette.error : theme.palette.info;
  return (
    <Box
      data-testid="whats-happening-calculation"
      sx={{
        mt: 1,
        px: 1.25,
        py: 0.75,
        maxWidth: 560,
        borderRadius: 1.25,
        border: '1px solid',
        borderColor: alpha(accent.main, 0.3),
        bgcolor: alpha(theme.palette.background.paper, 0.8),
      }}
    >
      {calculation.rows.map((row) => (
        <Box
          key={row.label}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            py: 0.4,
            ...(row.kind === 'result'
              ? { borderTop: '1px solid', borderColor: alpha(theme.palette.text.primary, 0.3), mt: 0.25, pt: 0.6 }
              : {}),
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: row.kind === 'result' ? 800 : 500,
              color: row.kind === 'limit' ? 'text.secondary' : 'text.primary',
            }}
          >
            {row.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: row.kind === 'result' ? 800 : 600,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
              color: row.kind === 'limit' ? 'text.secondary' : 'text.primary',
            }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
      <Typography variant="body2" sx={{ fontWeight: 800, color: accent.dark, mt: 0.5 }}>
        {calculation.verdict}
      </Typography>
    </Box>
  );
}

export function TabWhatsHappeningBanner({
  narrative,
  title = "What's happening",
  rich = false,
  onNavigate,
}: {
  narrative: TabNarrative;
  title?: string;
  /** When true, prefer short supporting bullets (Device tab). */
  rich?: boolean;
  /** Enables the narrative's checkLink ("Open the Income tab"). */
  onNavigate?: (key: ApplicationTabKey) => void;
}) {
  const theme = useTheme();
  const [techOpen, setTechOpen] = useState(false);
  const technical = (narrative.technicalDetails ?? []).filter((t) => t.trim());
  const bullets = narrative.bullets.filter((b) => b.trim());
  // Keep the banner when there is an operator-facing failure bullet (e.g. Mitek),
  // even if the overall tone was success from another check.
  if (narrative.tone === 'success' && bullets.length === 0) return null;

  const supportLines = buildTabWhatsHappeningSupportLines(narrative, rich);
  const isError = narrative.tone === 'error';
  // With a worked calculation the order is: why → the numbers → notes about them.
  const detailLine = narrative.detail?.trim() || null;
  const linesBeforeCalc = narrative.calculation ? (detailLine ? [detailLine] : []) : supportLines;
  const linesAfterCalc = narrative.calculation ? bullets : [];

  const renderLine = (line: string) => (
    <Typography
      key={line}
      component="div"
      variant="body2"
      sx={{
        lineHeight: 1.5,
        display: 'flex',
        gap: 0.75,
        color: isError ? 'error.dark' : 'text.primary',
        opacity: isError ? 0.92 : 1,
        fontWeight: isError ? 500 : 400,
        mt: 0.35,
        '&::before': {
          content: '"•"',
          flexShrink: 0,
          fontWeight: 800,
          color: isError ? 'error.main' : 'text.secondary',
        },
      }}
    >
      {line}
    </Typography>
  );

  return (
    <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
      <ApplicationSectionHeader
        title={title}
        icon={<CampaignRoundedIcon fontSize="small" />}
        mb={1}
      />
      <Alert
        severity={severityOf(narrative.tone)}
        sx={{
          borderRadius: 1.5,
          py: 0.85,
          ...(isError ? pdlDeclineAlertPulseSx(theme) : {}),
          '& .MuiAlert-message': { width: '100%' },
        }}
        icon={false}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            lineHeight: 1.4,
            mb: supportLines.length ? 0.65 : 0,
            color: isError ? 'error.dark' : 'inherit',
          }}
        >
          {narrative.headline}
        </Typography>
        {linesBeforeCalc.map(renderLine)}
        {narrative.calculation ? (
          <NarrativeCalculation calculation={narrative.calculation} isError={isError} />
        ) : null}
        {linesAfterCalc.length > 0 ? <Box sx={{ mt: 0.75 }}>{linesAfterCalc.map(renderLine)}</Box> : null}
        {(narrative.checkLink && onNavigate) || technical.length > 0 ? (
          <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
            {narrative.checkLink && onNavigate ? (
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => onNavigate(narrative.checkLink!.tab)}
                sx={{ fontWeight: 700, fontSize: '0.8125rem' }}
              >
                {narrative.checkLink.label} →
              </Link>
            ) : null}
            {technical.length > 0 ? (
              <Link
                component="button"
                type="button"
                underline="hover"
                color="text.secondary"
                aria-expanded={techOpen}
                onClick={() => setTechOpen((v) => !v)}
                sx={{ fontWeight: 700, fontSize: '0.8125rem', display: 'inline-flex', alignItems: 'center' }}
              >
                Technical details
                <ExpandMoreRoundedIcon
                  fontSize="small"
                  sx={{ transform: techOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}
                />
              </Link>
            ) : null}
          </Stack>
        ) : null}
        {technical.length > 0 ? (
          <Collapse in={techOpen}>
            <Box data-testid="whats-happening-technical" sx={{ mt: 0.75, pl: 1.25, borderLeft: '2px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                For risk and development teams
              </Typography>
              {technical.map((line) => (
                <Typography key={line} variant="caption" component="div" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                  {line}
                </Typography>
              ))}
            </Box>
          </Collapse>
        ) : null}
      </Alert>
    </Paper>
  );
}
